import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaUserCircle, FaChevronDown } from "react-icons/fa";
import "./Header.css";
import { MyContext } from "../Context/MyContext";
import { lockScroll, unlockScroll } from "../../Utils/scrollLock";
import NotificationBell from "./Notification/NotificationBell";
import { SITE_LOGO, SITE_LOGO_ALT } from "../../Utils/imageUtils";

export default function Header() {
  const {
    brands,
    fuel_type,
    transmission,
    seats,
    body_type,
    header: headerConfig,
  } = useContext(MyContext);

  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);
  const buyHoverTimer = useRef(null);

  const [activeMenu, setActiveMenu] = useState("brands");
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showBuyMenu, setShowBuyMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(headerConfig.auth?.tokenKey || "cftoken");
    setIsLoggedIn(!!token);
  }, [location.pathname, headerConfig.auth?.tokenKey]);

  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth <= 900);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      lockScroll();
      return () => unlockScroll();
    }
  }, [mobileOpen]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setMegaOpen(false);
      }
    };
    if (megaOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [megaOpen]);

  useEffect(() => {
    setMegaOpen(false);
    setMobileOpen(false);
    setShowBuyMenu(false);
  }, [location.pathname]);

  const menuData = {
    brands: { title: "Browse by Brand", shortTitle: "Brand", data: brands, query: "brand" },
    body: { title: "Browse by Body Type", shortTitle: "Body Type", data: body_type, query: "body" },
    fuel: { title: "Browse by Fuel Type", shortTitle: "Fuel", data: fuel_type, query: "fuel" },
    transmission: {
      title: "Browse by Transmission",
      shortTitle: "Transmission",
      data: transmission,
      query: "transmission",
    },
    seats: { title: "Browse by Seats", shortTitle: "Seats", data: seats, query: "seats" },
  };

  const linkNavItems = (headerConfig.navItems || []).filter((item) => item.type === "link");
  const megaNavItem = (headerConfig.navItems || []).find((item) => item.type === "mega");
  const activeCategory = menuData[activeMenu];

  const isActivePath = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isBuyActive =
    location.pathname === "/cars" || location.pathname.startsWith("/cars/");

  const handleNavigate = (queryKey, id) => {
    navigate(`/cars?${queryKey}=${id}`);
    setMegaOpen(false);
    setMobileOpen(false);
    setShowBuyMenu(false);
  };

  const handleMainNavigate = (path) => {
    navigate(path);
    setMobileOpen(false);
    setShowBuyMenu(false);
    setMegaOpen(false);
  };

  const handleBuyClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isMobile) {
      setMobileOpen(true);
      setShowBuyMenu(true);
      return;
    }
    navigate(megaNavItem?.path || "/cars");
    setMegaOpen(false);
  };

  const openMobileBuyMenu = () => {
    setMobileOpen(true);
    setShowBuyMenu(true);
  };

  const handleBuyEnter = () => {
    if (buyHoverTimer.current) clearTimeout(buyHoverTimer.current);
    if (!isMobile) setMegaOpen(true);
  };

  const handleBuyLeave = () => {
    if (isMobile) return;
    buyHoverTimer.current = setTimeout(() => setMegaOpen(false), 220);
  };

  const handleAuthClick = () => {
    navigate(isLoggedIn ? "/profile" : "/login");
    setMobileOpen(false);
  };

  const authLabel = isLoggedIn
    ? headerConfig.auth?.profileLabel || "Profile"
    : headerConfig.auth?.loginLabel || "Login";

  const authKicker = isLoggedIn ? "My account" : "Sign in";

  return (
    <header
      className={`header ${isScrolled ? "header--scrolled" : ""}`}
      ref={headerRef}
    >
      {mobileOpen && isMobile && (
        <div
          className="mobile-backdrop"
          onClick={() => {
            setMobileOpen(false);
            setShowBuyMenu(false);
          }}
          aria-hidden="true"
        />
      )}

      <div className="header-shell">
        <div className="header-inner">
          <div className="header-left">
            <button
              type="button"
              className="mobile-menu"
              onClick={() => {
                setMobileOpen((prev) => !prev);
                setShowBuyMenu(false);
              }}
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
            >
              <span className={`hamburger ${mobileOpen ? "open" : ""}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>

            <div
              className="logo"
              onClick={() => handleMainNavigate(headerConfig.logo?.path || "/")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" && handleMainNavigate(headerConfig.logo?.path || "/")
              }
              aria-label={headerConfig.logo?.alt || SITE_LOGO_ALT}
            >
              <img
                src={SITE_LOGO}
                alt={headerConfig.logo?.alt || SITE_LOGO_ALT}
                className="logo-img"
              />
            </div>
          </div>

          {isMobile && megaNavItem && (
            <div className="header-center-mobile">
              <button
                type="button"
                className={`header-buy-mobile ${isBuyActive || showBuyMenu ? "active" : ""}`}
                onClick={openMobileBuyMenu}
                aria-expanded={showBuyMenu}
              >
                {megaNavItem.label}
                <FaChevronDown className={`header-buy-mobile-chevron ${showBuyMenu ? "open" : ""}`} />
              </button>
            </div>
          )}

          <nav className={`head ${mobileOpen ? "show-mobile" : ""}`} aria-label="Main">
            <button
              type="button"
              className="mobile-close"
              onClick={() => {
                setMobileOpen(false);
                setShowBuyMenu(false);
              }}
              aria-label="Close menu"
            >
              ×
            </button>

            {!isMobile && megaNavItem && (
              <div
                className="buy-menu-container"
                onMouseEnter={handleBuyEnter}
                onMouseLeave={handleBuyLeave}
              >
                <button
                  type="button"
                  className={`head-item nav-link buy-trigger ${megaOpen || isBuyActive ? "active" : ""}`}
                  onClick={handleBuyClick}
                  aria-expanded={megaOpen}
                >
                  <span className="nav-link-text">{megaNavItem.label}</span>
                  <FaChevronDown className={`buy-chevron ${megaOpen ? "open" : ""}`} />
                </button>
              </div>
            )}

            {!isMobile &&
              linkNavItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`head-item nav-link ${isActivePath(item.path) ? "active" : ""}`}
                  onClick={() => handleMainNavigate(item.path)}
                >
                  <span className="nav-link-text">{item.label}</span>
                </button>
              ))}

            {isMobile && (
              <div className="mobile-content">
                {!showBuyMenu ? (
                  <>
                    <div className="mobile-drawer-logo">
                      <img
                        src={SITE_LOGO}
                        alt={headerConfig.logo?.alt || SITE_LOGO_ALT}
                        className="logo-img logo-img--mobile"
                      />
                    </div>

                    {megaNavItem && (
                      <button
                        type="button"
                        className={`mobile-menu-item mobile-menu-item--buy ${isBuyActive || showBuyMenu ? "active" : ""}`}
                        onClick={handleBuyClick}
                      >
                        {megaNavItem.label}
                        <FaChevronDown className={`mobile-chevron ${showBuyMenu ? "open" : ""}`} />
                      </button>
                    )}

                    {linkNavItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`mobile-menu-item ${isActivePath(item.path) ? "active" : ""}`}
                        onClick={() => handleMainNavigate(item.path)}
                      >
                        {item.label}
                      </button>
                    ))}

                    <button
                      type="button"
                      className="mobile-menu-item mobile-auth-item"
                      onClick={handleAuthClick}
                    >
                      {authLabel}
                    </button>
                  </>
                ) : (
                  <div className="mobile-subpage">
                    <div className="mobile-subpage-head">
                      <button
                        type="button"
                        className="mobile-back"
                        onClick={() => setShowBuyMenu(false)}
                      >
                        {headerConfig.megaMenu?.backLabel || "← Back"}
                      </button>
                      <button
                        type="button"
                        className="mobile-view-all"
                        onClick={() => handleMainNavigate(megaNavItem?.path || "/cars")}
                      >
                        View all
                      </button>
                    </div>

                    <div className="mobile-sub-title">
                      {megaNavItem?.label || "Buy Car"}
                    </div>

                    <div className="mobile-buy-tabs" role="tablist" aria-label="Browse categories">
                      {Object.keys(menuData).map((key) => (
                        <button
                          key={key}
                          type="button"
                          role="tab"
                          aria-selected={activeMenu === key}
                          className={`mobile-buy-tab ${activeMenu === key ? "active" : ""}`}
                          onClick={() => setActiveMenu(key)}
                        >
                          {menuData[key].shortTitle}
                        </button>
                      ))}
                    </div>

                    <div className="mobile-buy-panel">
                      <p className="mobile-buy-panel-title">{activeCategory.title}</p>
                      <div className="mobile-category-grid">
                        {activeCategory.data.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className="mobile-category-item"
                            onClick={() => handleNavigate(activeCategory.query, item.id)}
                          >
                            {item.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="header-right">
            {isLoggedIn && <NotificationBell />}
            <button
              type="button"
              className={`header-auth-block ${isMobile ? "mobile-login-top" : ""}`}
              onClick={handleAuthClick}
            >
              <FaUserCircle className="header-auth-icon" aria-hidden />
              {!isMobile && (
                <span className="header-auth-text">
                  <small>{authKicker}</small>
                  <strong>{authLabel}</strong>
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {!isMobile && (
        <>
          <div
            className={`mega-backdrop ${megaOpen ? "is-visible" : ""}`}
            onClick={() => setMegaOpen(false)}
            aria-hidden={!megaOpen}
          />
          <div
            className={`mega-menu-panel ${megaOpen ? "is-visible" : ""}`}
            onMouseEnter={handleBuyEnter}
            onMouseLeave={handleBuyLeave}
            aria-hidden={!megaOpen}
          >
            <div className="mega-menu-inner">
              <aside className="mega-sidebar">
                <p className="mega-sidebar-label">Categories</p>
                {Object.keys(menuData).map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`mega-title ${activeMenu === key ? "active" : ""}`}
                    onClick={() => setActiveMenu(key)}
                  >
                    <span className="mega-title-text">{menuData[key].shortTitle}</span>
                    <span className="mega-title-count">{menuData[key].data.length}</span>
                  </button>
                ))}
              </aside>

              <div className="mega-content">
                <div className="mega-content-header">
                  <h3>{activeCategory.title}</h3>
                  <span className="mega-item-count">
                    {activeCategory.data.length} options
                  </span>
                </div>

                <div className="mega-right">
                  {activeCategory.data.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="mega-item"
                      onClick={() => handleNavigate(activeCategory.query, item.id)}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
